import { nativeOffline } from "@/lib/offlineNative";
import { HandCoins, Moon } from "lucide-react";
import ToolButtons from "@/components/mm/ToolButtons";
import { useEffect, useState } from "react";
import { Landmark, NotebookPen, Plus, Home, Calculator as CalculatorIcon, Activity, TrendingUp } from "lucide-react";
import PdfEditor from "@/components/mm/PdfEditor";
import SyncStatus from "@/components/mm/SyncStatus";
import { Modal } from "@/components/ui-mm/Modal";
import Header from "@/components/mm/Header";
import HeroCard from "@/components/mm/HeroCard";
import QuoteCard from "@/components/mm/QuoteCard";
import AddExpense from "@/components/mm/AddExpense";
import CategoryEditor from "@/components/mm/CategoryEditor";
import Planning from "@/components/mm/Planning";
import FinancialPulse from "@/components/mm/FinancialPulse";
import History from "@/components/mm/History";
import Charts from "@/components/mm/Charts";
import { AverageSpending, CashBreakdown, Unnecessary } from "@/components/mm/Insights";
import { RecurringDialog, RecurringDue } from "@/components/mm/Recurring";
import Borrowed from "@/components/mm/Borrowed";
import Zakat from "@/components/mm/Zakat";
import IncomeDialog from "@/components/mm/IncomeDialog";
import SavingsDialog from "@/components/mm/SavingsDialog";
import SettingsDialog from "@/components/mm/SettingsDialog";
import Calculator from "@/components/mm/Calculator";
import Notepad from "@/components/mm/Notepad";
import DownloadSection from "@/components/mm/DownloadSection";
import QuickStartGuide from "@/components/mm/QuickStartGuide";
import { Link } from "react-router-dom";
import { useGuideHistory } from "@/hooks/useGuideHistory";
import Onboarding from "@/components/mm/Onboarding";
import { Brand } from "@/components/mm/Brand";
import { useData } from "@/context/DataContext";
import { t } from "@/lib/i18n";

const Footer = () => (
  <footer className="mm-card mm-rise flex flex-col items-center gap-2 text-center text-xs text-[var(--mu)]" data-testid="app-footer">
    <Brand size="text-lg" mark="h-8 w-8" />
    <p className="text-[11px] tracking-[0.12em]">{t("Every Dollar. Under Control.")}</p>
    <p className="font-semibold text-[var(--tx)]" data-testid="footer-version">{t("Version 3.3 Free-First")} · Built by MD FIROZ ALI</p>
    <p>Student of IT at AIBI Higher Education, Australia</p>
    <a className="font-medium text-[var(--ac)] underline-offset-2 hover:underline" href="mailto:budgetpulse.enquiry@gmail.com" data-testid="footer-email">budgetpulse.enquiry@gmail.com</a>
    <div className="flex gap-4"><Link className="font-medium text-[var(--ac)] hover:underline" to="/terms">Terms of Service</Link><Link className="font-medium text-[var(--ac)] hover:underline" to="/privacy">Privacy Policy</Link></div>
  </footer>
);

export default function Dashboard() {
  const { db, load, loadError } = useData();
  useGuideHistory();
  const soundEnabled = !!db && db.settings.prefs.sound !== false;
  useEffect(() => {
    if (!soundEnabled) return;
    let ctx;
    const play = (event) => {
      if (!event.target.closest("button,[role=button]")) return;
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        ctx ||= new AudioContext();
        if (ctx.state === "suspended") ctx.resume();
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.type = "sine"; osc.frequency.setValueAtTime(660, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.045);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime); gain.gain.exponentialRampToValueAtTime(0.035, ctx.currentTime + 0.008); gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.075);
        osc.connect(gain); gain.connect(ctx.destination); osc.start(); osc.stop(ctx.currentTime + 0.08);
      } catch { /* audio is optional */ }
    };
    document.addEventListener("click", play, true);
    return () => { document.removeEventListener("click", play, true); if (ctx) ctx.close().catch(() => {}); };
  }, [soundEnabled]);
  const [dlg, setDlg] = useState(null);
  const [expenseScan, setExpenseScan] = useState(false);
  const spending = (scan = false) => { setExpenseScan(scan); setDlg("expense"); };
  const [setTab, setSetTab] = useState("general");
  const open = (k) => () => setDlg(k);
  const close = () => setDlg(null);
  const settings = (tab) => { setSetTab(tab || "general"); setDlg("settings"); };

  if (!db) return <main className="mx-auto max-w-lg space-y-4 p-5" aria-busy={!loadError} data-testid="dashboard-loading"><Brand />{loadError ? <><p role="alert">{loadError}</p><button className="mm-btn" onClick={() => load().catch(() => {})}>Retry loading data</button></> : <><p role="status">Loading your account…</p>{[1, 2, 3].map((n) => <div key={n} className="h-24 animate-pulse rounded-2xl bg-[var(--bd)]" />)}</>}</main>;
  const muslim = db.settings.prefs.faith !== "other";

  return (
    <div className="min-h-[100dvh]" data-testid="dashboard">
      <Header onDesign={open("design")} onSettings={settings} />
      <div className="mx-auto max-w-[1440px] px-3 pt-3 sm:px-6 xl:px-8">
        <SyncStatus />{db.environment === "staging" && <p role="status" className="text-sm font-semibold text-[var(--wn)]">Staging · Test data only</p>}
      </div>

      <main className="mx-auto grid max-w-[1440px] grid-cols-12 gap-4 px-3 py-3 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:gap-6 sm:px-6 xl:gap-7 xl:px-8">
        <div className="col-span-12 min-w-0 space-y-4 sm:space-y-6 lg:col-span-7">
          <HeroCard onIncome={open("income")} onSavings={open("savings")} />
          <ToolButtons onIncome={open("income")} onExpense={() => spending()} onSavings={open("savings")} onRecurring={open("rec")} onNotepad={open("notepad")} onPdfEditor={open("pdf-editor")} />
          <QuoteCard />

        </div>
        <div className="col-span-12 min-w-0 space-y-4 sm:space-y-6 lg:col-span-5">
          <Charts />
          <AverageSpending />
          <Unnecessary />
          <CashBreakdown />
        </div>
        {nativeOffline ? (
          <div className="col-span-12 grid grid-cols-2 items-start gap-3 sm:gap-6" aria-label="Financial planning and calculators">
            {[
              ["pulse", Activity, t("Financial pulse")],
              ["forecast", TrendingUp, t("Cash-flow forecast")],
              ["borrowed", HandCoins, t("Borrowed items")],
              ...(muslim ? [["zakat", Moon, t("Zakat calculator")]] : []),
            ].map(([key, Icon, title]) => (
              <button key={key} type="button" onClick={open(key)} className="mm-card flex h-[112px] min-w-0 flex-col items-center justify-center gap-2 text-center sm:h-[128px]" data-testid={`planning-${key}-open`}>
                <Icon className="h-6 w-6 shrink-0 text-[var(--ac)]" aria-hidden="true" />
                <span className="text-xs font-semibold leading-tight sm:text-base">{title}</span>
              </button>
            ))}
          </div>
        ) : (
        <div className="col-span-12 grid grid-cols-2 items-start gap-3 sm:gap-6" aria-label="Financial planning and calculators">
          <details className="mm-card min-w-0 !p-0">
            <summary className="flex h-[112px] cursor-pointer list-none flex-col items-center justify-center gap-2 p-2 text-center marker:hidden sm:h-[128px] [&::-webkit-details-marker]:hidden">
              <Activity className="h-6 w-6 shrink-0 text-[var(--ac)]" aria-hidden="true" />
              <span className="text-xs font-semibold leading-tight sm:text-base">{t("Financial pulse")}</span>
            </summary>
            <div className="border-t border-[var(--bd)] p-3 sm:p-5"><FinancialPulse /></div>
          </details>
          <details className="mm-card min-w-0 !p-0">
            <summary className="flex h-[112px] cursor-pointer list-none flex-col items-center justify-center gap-2 p-2 text-center marker:hidden sm:h-[128px] [&::-webkit-details-marker]:hidden">
              <TrendingUp className="h-6 w-6 shrink-0 text-[var(--ac)]" aria-hidden="true" />
              <span className="text-xs font-semibold leading-tight sm:text-base">{t("Cash-flow forecast")}</span>
            </summary>
            <div className="border-t border-[var(--bd)] p-3 sm:p-5"><Planning /></div>
          </details>
          <Borrowed />
          {muslim && <Zakat />}
        </div>
        )}
        <div className="col-span-12 lg:col-span-8"><History /></div>
        <div className="col-span-12">
          <DownloadSection />
        </div>
        <div className="col-span-12">
          <Footer />
        </div>
      </main>
      <nav className="bp-bottom-nav" aria-label="Main navigation">
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><Home size={20} /><span>{t("Home")}</span></button>
        <button onClick={() => spending()}><Plus size={22} /><span>{t("Expense")}</span></button>
        <button onClick={open("savings")}><Landmark size={20} /><span>{t("Savings")}</span></button>
        <button onClick={open("notepad")}><NotebookPen size={20} /><span>Notes</span></button>
        <button onClick={open("calc")} data-testid="bottom-nav-calculator"><CalculatorIcon size={20} /><span>{t("Calculator")}</span></button>
      </nav>
      {nativeOffline && <>
        <Modal fullScreen open={dlg === "pulse"} onClose={close} title={t("Financial pulse")} testId="financial-pulse-fullscreen"><FinancialPulse /></Modal>
        <Modal fullScreen open={dlg === "forecast"} onClose={close} title={t("Cash-flow forecast")} testId="forecast-fullscreen"><Planning /></Modal>
        <Modal fullScreen open={dlg === "borrowed"} onClose={close} title={t("Borrowed items")} testId="borrowed-fullscreen"><Borrowed contentOnly /></Modal>
        {muslim && <Modal fullScreen open={dlg === "zakat"} onClose={close} title={t("Zakat calculator")} testId="zakat-fullscreen"><Zakat contentOnly /></Modal>}
      </>}
      <Modal open={dlg === 'expense'} onClose={close} title={t("Add expense")} testId="quick-expense-dialog"><AddExpense initialScan={expenseScan} onEditCats={open("cats")} onRecurring={open("rec")} /></Modal>
      <Modal open={dlg === 'guide'} onClose={close} title="Quick Guide"><QuickStartGuide manual onClose={close} /></Modal>
      <IncomeDialog open={dlg === "income"} onClose={close} />
      <SavingsDialog open={dlg === "savings"} onClose={close} />
      <SettingsDialog open={dlg === "settings"} tab={setTab} onClose={close} onGuide={open("guide")} />
      <SettingsDialog open={dlg === "design"} designOnly onClose={close} onGuide={open("guide")} />
      <CategoryEditor open={dlg === "cats"} onClose={close} />
      <RecurringDialog open={dlg === "rec"} onClose={close} />
      <Calculator open={dlg === "calc"} onClose={close} />
      <Notepad open={dlg === "notepad"} onClose={close} />
      {dlg === "pdf-editor" && <div className="fixed inset-0 z-[120] overflow-y-auto bg-[var(--bg)] p-3 pt-[max(1rem,env(safe-area-inset-top))] text-[var(--tx)] sm:p-6" role="dialog" aria-modal="true" aria-label="PDF Editor" data-testid="pdf-editor-workspace"><div className="mx-auto max-w-5xl"><div className="mb-3 flex items-center justify-between gap-3"><h1 className="text-xl font-bold">BudgetPulse PDF Editor</h1><button type="button" className="mm-btn ghost" onClick={close} aria-label="Close PDF Editor">Close</button></div><PdfEditor /></div></div>}
      <RecurringDue />
      <Onboarding />
    </div>
  );
}
